<template>
    <div v-if="show" class="modal-overlay" @click.self="closeModal">
        <div class="modal-container">
            <div class="modal-header">
                <h3>Nouveau fournisseur</h3>
                <button class="modal-close" @click="closeModal">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>
            </div>

            <div class="modal-body">
                <form @submit.prevent="submitForm">
                    <div class="form-group">
                        <label for="name">Nom du fournisseur *</label>
                        <input id="name" v-model.trim="form.name" type="text" class="form-control"
                            :class="{ 'input-error': validationErrors.name }" required placeholder="Nom complet" />
                        <span v-if="validationErrors.name" class="field-error">
                            {{ validationErrors.name[0] }}
                        </span>
                    </div>

                    <div class="form-row">
                        <div class="form-group">
                            <label for="email">Email</label>
                            <input id="email" v-model.trim="form.email" type="email" class="form-control"
                                :class="{ 'input-error': validationErrors.email }" placeholder="exemple@domaine.com" />
                            <span v-if="validationErrors.email" class="field-error">
                                {{ validationErrors.email[0] }}
                            </span>
                        </div>
                        <div class="form-group">
                            <label for="phone">Téléphone</label>
                            <input id="phone" v-model.trim="form.phone" type="text" class="form-control"
                                :class="{ 'input-error': validationErrors.phone }" placeholder="+221 77 000 00 00" />
                            <span v-if="validationErrors.phone" class="field-error">
                                {{ validationErrors.phone[0] }}
                            </span>
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="contact_person">Personne de contact</label>
                        <input id="contact_person" v-model.trim="form.contact_person" type="text" class="form-control"
                            :class="{ 'input-error': validationErrors.contact_person }"
                            placeholder="Nom de la personne" />
                        <span v-if="validationErrors.contact_person" class="field-error">
                            {{ validationErrors.contact_person[0] }}
                        </span>
                    </div>

                    <div class="form-group checkbox">
                        <label class="checkbox-label">
                            <input type="checkbox" v-model="form.active" />
                            Actif
                        </label>
                    </div>

                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeModal" :disabled="isSubmitting">
                            Annuler
                        </button>
                        <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
                            <span v-if="isSubmitting" class="spinner-small"></span>
                            <span v-else>Enregistrer</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, watch } from 'vue'
import supplierService from '@/services/supplierService'
import { useToastStore } from '@/store/modules/toastStore'

export default {
    name: 'CreateSupplierModal',
    props: {
        show: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close', 'created'],
    setup(props, { emit }) {
        const toastStore = useToastStore()

        const form = ref({
            name: '',
            email: '',
            phone: '',
            contact_person: '',
            active: true
        })
        const validationErrors = ref({})
        const isSubmitting = ref(false)

        const resetForm = () => {
            form.value = {
                name: '',
                email: '',
                phone: '',
                contact_person: '',
                active: true
            }
            validationErrors.value = {}
        }

        const closeModal = () => {
            if (!isSubmitting.value) {
                resetForm()
                emit('close')
            }
        }

        const submitForm = async () => {
            if (isSubmitting.value) return
            validationErrors.value = {}
            isSubmitting.value = true
            try {
                const payload = {
                    name: form.value.name,
                    email: form.value.email || null,
                    phone: form.value.phone || null,
                    contact_person: form.value.contact_person || null,
                    active: !!form.value.active
                }
                const response = await supplierService.createSupplier(payload)
                toastStore.addToast({
                    type: 'success',
                    message: 'Fournisseur créé avec succès',
                    duration: 4000
                })
                resetForm()
                emit('created', response.data?.data || response.data)
                emit('close')
            } catch (error) {
                if (error && error.status === 422 && error.errors) {
                    validationErrors.value = error.errors
                    toastStore.addToast({
                        type: 'error',
                        message: error.message || 'Erreurs de validation',
                        duration: 4000
                    })
                } else {
                    toastStore.addToast({
                        type: 'error',
                        message: error?.message || 'Erreur lors de la création du fournisseur',
                        duration: 4000
                    })
                }
            } finally {
                isSubmitting.value = false
            }
        }

        watch(() => props.show, (val) => {
            if (val) resetForm()
        })

        return {
            form,
            validationErrors,
            isSubmitting,
            closeModal,
            submitForm
        }
    }
}
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
}

.modal-container {
    background: white;
    border-radius: 0.75rem;
    width: 100%;
    max-width: 560px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
}

.modal-close {
    background: none;
    border: none;
    cursor: pointer;
    color: #718096;
    padding: 0.5rem;
    border-radius: 0.375rem;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.modal-close:hover {
    background: #f7fafc;
    color: #2d3748;
}

.modal-body {
    padding: 1.5rem;
    overflow-y: auto;
}

.form-group {
    margin-bottom: 1rem;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

.form-group label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #4a5568;
    margin-bottom: 0.5rem;
}

.form-control {
    width: 100%;
    padding: 0.625rem 1rem;
    border: 1.5px solid #e2e8f0;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    transition: all 0.2s;
    font-family: inherit;
}

.form-control:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.form-control.input-error {
    border-color: #fc8181;
}

.checkbox {
    display: flex;
    align-items: center;
}

.checkbox-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.9rem;
    color: #374151;
    user-select: none;
}

.field-error {
    display: block;
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #e53e3e;
}

.modal-footer {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    padding-top: 1.25rem;
    margin-top: 1.25rem;
    border-top: 1px solid #e2e8f0;
}

.btn {
    padding: 0.625rem 1.25rem;
    border: none;
    border-radius: 0.5rem;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-secondary {
    background: #f7fafc;
    color: #4a5568;
    border: 1px solid #e2e8f0;
}

.btn-secondary:hover:not(:disabled) {
    background: #edf2f7;
    border-color: #cbd5e0;
}

.btn-primary {
    background: linear-gradient(135deg, #0A5C6B 0%, #094854 100%);
    color: white;
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(10, 92, 107, 0.4);
}

.spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 640px) {
    .form-row {
        grid-template-columns: 1fr;
    }

    .modal-container {
        max-width: 100%;
        margin: 1rem;
    }

    .modal-footer {
        flex-direction: column-reverse;
    }

    .btn {
        width: 100%;
    }
}
</style>
